import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug } from '../lib/cms';
import Gallery from '../components/Gallery';
import SiteMapViewer from '../components/SiteMapViewer';

export default function About() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    async function loadData() {
      const page = await fetchPageBySlug('om-campen');
      setPageData(page);
    }
    loadData();
  }, []);

  const metaTitle = pageData?.meta_title?.da || "Om Campen Camping - Fredelig campingplads på Samsø";
  const metaDesc = pageData?.meta_description?.da || "Læs mere om Campen Camping på Samsø. Beliggende på Sydøen med egen badebro, fantastiske solnedgange, gode fiskeforhold og hyggelige rammer.";

  return (
    <div className="py-12 bg-stone-50 min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
      </Helmet>

      <div className="mx-auto max-w-7xl px-6">
        {/* Intro */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#67aedd] text-xs font-bold uppercase tracking-wider">
            Vores historie & rammer
          </span>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-[#0f0d0e] mt-1 mb-4">
            Om Campen Camping
          </h1>
          <p className="text-stone-600 text-sm md:text-base leading-relaxed">
            Læs historien om vores fredelige campingplads på Sydsamsø, vores fantastiske beliggenhed og de mange oplevelser, der venter dig på øen.
          </p>
        </div>

        {/* Verbatim Scraped Text - Section 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="relative rounded-2xl overflow-hidden aspect-4/3 shadow-sm bg-stone-100">
            <img
              src="https://assets.auxodev.dk/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/image33-scaled.jpeg"
              alt="Campen Camping udsigt"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex flex-col justify-center">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-[#0f0d0e] mb-6">
              Fredelig atmosfære og overdådig havudsigt
            </h2>
            <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
              <p>
                Samsø er en charmerende ferieø midt i Danmark, hvor livet går lidt langsommere, og naturen får lov at fylde. Her kan man cykle gennem de små landsbyer, nyde lokale råvarer, bade ved de rene strande og opleve den afslappede østemning, der gør Samsø til et perfekt sted at koble helt af.
              </p>
              <p>
                Øens sydligste campingplads er smukt beliggende med en overdådig havudsigt mod vest. I det fjerne ses Endelave og Horsens Fjord, mens man mod nord kan se helt til Århus.
              </p>
              <p>
                Pladsen er meget fredelig og hyggelig, og der er god plads til den enkelte campist. Der er også mulighed for at samles i flok, hvis man f.eks er en gruppe, der er på en fælles tur. Der er tradition for, at man vælger pladsen her til biltræf, golfture eller i forbindelse med kursusvirksomhed.
              </p>
            </div>
          </div>
        </div>

        {/* Verbatim Scraped Text - Section 2 (Badebro & Fiskeri) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16 lg:flex-row-reverse">
          <div className="flex flex-col justify-center lg:order-2">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-[#0f0d0e] mb-6">
              Badebro, solnedgange og fine fiskeforhold
            </h2>
            <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
              <p>
                Pladsen har sin egen badebro og er berømt for sine vidunderlige solnedgange.
              </p>
              <p>
                Der er fine fiskeforhold ud for pladsen. Der fiskes både sild og ørred og i sæsonen er der hornfisk og hummere i det rene vand.
              </p>
              <p>
                Pladsen har et stort fælles hus med alle køkkenfaciliteter samt kønsopdelte baderum og toiletter. Der er også en tømningsplads og skønne områder med borde og bænke.
              </p>
            </div>
          </div>

          <div className="relative rounded-2xl overflow-hidden aspect-4/3 shadow-sm bg-stone-100 lg:order-1">
            <img
              src="https://assets.auxodev.dk/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/image31-scaled.jpeg"
              alt="Badebro og solnedgang"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Verbatim Scraped Text - Section 3 (Sydøens attraktioner) */}
        <div className="bg-white rounded-2xl border border-stone-200/60 p-8 md:p-12 mb-16 shadow-sm">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-[#0f0d0e] mb-6 text-center">
            Oplevelser og attraktioner på Sydøen
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-stone-600 text-sm md:text-base leading-relaxed">
            <div>
              <p className="mb-4">
                Blandt Sydøens hovedattraktioner er der de traditionelle vindmøller ’Kolby Mølle’ samt ’Brundby Stubmølle’. Begge rager flot op i landskabet og giver jer mulighed for at komme tæt på øens lange historie som landbrugssamfund.
              </p>
              <p>
                Er I på udkig efter sjove aktiviteter som børnefamilie, er det oplagt at tage ud til Falkecenter Samsø i Pillemark, ride på islandske heste ved Nørreskiftegaard eller opleve Fredensdal, som er et husmandssted fra 1904.
              </p>
            </div>
            <div>
              <p className="mb-4">
                På Landkrabben kan hele familien deltage i naturformidling og maritime aktiviteter. 
              </p>
              <p>
                Hvis I er på parferie eller går efter oplevelser, der får jer ned i tempo, er Sydøen et sandt mekka med gallerier, yoga og kurser i plantefarvning.
              </p>
            </div>
          </div>
        </div>

        {/* Pladsoversigt */}
        <div className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-[#67aedd] text-xs font-bold uppercase tracking-wider">
              Pladsoversigt
            </span>
            <h2 className="text-3xl font-serif font-bold text-[#0f0d0e] mt-1">
              Oversigt over Campen
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Her kan du se placeringen af vores hytter, teltpladser, campingvogne og fællesfaciliteter.
            </p>
          </div>
          <div className="bg-white rounded-3xl p-2 md:p-4 border border-stone-200/60 shadow-sm overflow-hidden max-w-3xl mx-auto">
            <SiteMapViewer 
              pdfUrl="/_assets/campen-camping-wayfinding-revideret-20-08.pdf"
              className="w-full"
            />
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-b from-stone-50 to-[#e8f3fa] text-stone-800 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden shadow-sm border border-stone-200/60">
          <div className="absolute inset-0 z-0 opacity-5">
            <img
              src="https://assets.auxodev.dk/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/hyt14-scaled.jpg"
              alt="Campen Camping"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-4">
              Vil du opleve Campen Camping med egne øjne?
            </h2>
            <p className="text-stone-600 text-sm md:text-base mb-8 leading-relaxed">
              Book dit næste ophold i dag, eller kontakt os hvis du har spørgsmål til pladsen, vores hytter eller leje af hele pladsen.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/feriehuse"
                className="bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-8 py-3.5 rounded-full shadow-md transition-colors text-sm"
              >
                <i className="fa-solid fa-calendar-days mr-2" /> Book dit ophold
              </Link>
              <Link
                to="/kontakt"
                className="bg-white hover:bg-stone-50 text-stone-800 border border-stone-200 font-semibold px-8 py-3.5 rounded-full transition-colors text-sm shadow-sm"
              >
                Kontakt os
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
